import { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";

const MedicalReportsManagement = () => {
  const [passportNumber, setPassportNumber] = useState("");
  const [medicalReport, setMedicalReport] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [formInfo, setFormInfo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jsonData, setJsonData] = useState("");

  const apiUrl = import.meta.env.VITE_API_URL;

   // Add modal styles
  // Add modal styles
  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '90%',
      maxWidth: '800px',
      maxHeight: '90vh',
      border: '1px solid #ccc',
      borderRadius: '8px',
      padding: '20px',
      overflowY: 'auto',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
    },
  };

  // Handle section changes
  const handleSectionChange = (index, field, value) => {
    const updatedSections = [...formInfo.sections];
    updatedSections[index][field] = value;
    setFormInfo({ ...formInfo, sections: updatedSections });
  };

  // Add new section
  const addNewSection = () => {
    setFormInfo({
      ...formInfo,
      sections: [
        ...formInfo.sections,
        {
          title: "",
          content: "",
          icon: "security" // default icon
        }
      ]
    });
  };

  // Remove section
  const removeSection = (index) => {
    const updatedSections = [...formInfo.sections];
    updatedSections.splice(index, 1);
    setFormInfo({ ...formInfo, sections: updatedSections });
  };

  // Handle form info update
  const handleUpdateFormInfo = async () => {
    try {
      await axios.patch(`${apiUrl}/form-info/medical-report/`, formInfo);
      setMessage("Form info updated successfully!");
      setIsModalOpen(false);
      fetchFormInfo(); // Refresh form info
    } catch (error) {
      setMessage("Error updating form info: " + error.message);
    }
  };
  
  // Fetch reports
  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/medical-reports/`);
      setReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setMessage("Failed to fetch reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch form info
  const fetchFormInfo = async () => {
    try {
      const response = await axios.get(`${apiUrl}/form-info/medical-report/`);
      setFormInfo(response.data);
      setJsonData(JSON.stringify(response.data, null, 2));
    } catch (error) {
      console.error("Error fetching form info:", error);
      setMessage("Failed to fetch form info. Please try again.");
    }
  };

  useEffect(() => {
    fetchReports();
    fetchFormInfo();
  }, []);

  // Handle file change
  const handleFileChange = (e) => {
    setMedicalReport(e.target.files[0]);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passportNumber || !medicalReport) {
      setMessage("Please provide a passport number and upload a medical report.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("passport_number", passportNumber);
    formData.append("medical_report", medicalReport);

    try {
      // Check if a report exists for the given passport number
      await axios.get(`${apiUrl}/medical-reports/${passportNumber}/`);
      // If found, update the report
      await axios.put(`${apiUrl}/medical-reports/${passportNumber}/`, formData);
      setMessage("Medical report updated successfully.");
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // If not found, create a new report
        try {
          await axios.post(`${apiUrl}/medical-reports/`, formData);
          setMessage("Medical report uploaded successfully.");
        } catch (createError) {
          setMessage("Error uploading the report. Please try again.");
        }
      } else {
        setMessage("Error updating the report. Please try again.");
      }
    } finally {
      setPassportNumber("");
      setMedicalReport(null);
      setLoading(false);
      fetchReports(); // Refresh the reports
    }
  };

  // Handle report selection
  const handleSelectReport = (id) => {
    setSelectedReports((prev) =>
      prev.includes(id) ? prev.filter((reportId) => reportId !== id) : [...prev, id]
    );
  };

  // Handle deleting reports
  const handleDeleteReports = async (idsToDelete = selectedReports) => {
    if (idsToDelete.length === 0) {
      setMessage("No reports selected for deletion.");
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        idsToDelete.map((id) => axios.delete(`${apiUrl}/medical-reports/${id}/`))
      );
      setMessage("Selected reports deleted successfully.");
      setSelectedReports([]);
      fetchReports(); // Refresh reports
    } catch (error) {
      setMessage("Error deleting reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Medical Reports Management</h2>

      {/* Feedback Message */}
      {message && (
        <p className={`mb-4 font-bold ${
          message.includes("success") ? "text-green-500" : "text-red-500"
        }`}>
          {message}
        </p>
      )}

      {/* Form Info Management */}
      <div className="mb-6 p-4 bg-blue-50 rounded-md">
        <h3 className="text-xl font-bold mb-2">Form Information</h3>
        <p className="mb-4">Manage the content displayed on the medical report page.</p>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Edit Form Content
        </button>
      </div>

      {/* Upload Section */}
      <div className="p-6 bg-green-50 border rounded-md shadow-md mb-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="passportNumber" className="block text-lg font-semibold mb-2">
              Passport Number:
            </label>
            <input
              type="text"
              id="passportNumber"
              value={passportNumber}
              onChange={(e) => setPassportNumber(e.target.value)}
              className="p-2 border rounded-md w-full"
              placeholder="Enter passport number"
            />
          </div>
          <div>
            <label htmlFor="medicalReport" className="block text-lg font-semibold mb-2">
              Upload Medical Report:
            </label>
            <input
              type="file"
              id="medicalReport"
              onChange={handleFileChange}
              className="p-2 border rounded-md w-full"
              accept=".pdf,.doc,.docx,.jpg,.png"
            />
          </div>
          <button
            type="submit"
            className={`w-full p-2 bg-green-500 text-white rounded-md ${loading && "opacity-50 cursor-not-allowed"}`}
            disabled={loading}
          >
            {loading ? "Uploading..." : "Upload Report"}
          </button>
        </form>
      </div>

      {/* Reports Table */}
      <div className="mb-4">
        <h3 className="text-xl font-bold mb-2">Existing Reports</h3>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <table className="w-full border">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2">Select</th>
                <th className="border px-4 py-2">Passport Number</th>
                <th className="border px-4 py-2">Report</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.passport_number}>
                  <td className="border px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedReports.includes(report.passport_number)}
                      onChange={() => handleSelectReport(report.passport_number)}
                    />
                  </td>
                  <td className="border px-4 py-2">{report.passport_number}</td>
                  <td className="border px-4 py-2">
                    <a
                      href={report.medical_report}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline"
                    >
                      View Report
                    </a>
                  </td>
                  <td className="border px-4 py-2">
                    <button
                      onClick={() => handleDeleteReports([report.passport_number])}
                      className="bg-red-500 text-white px-2 py-1 rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Multiple */}
      <button
        onClick={() => handleDeleteReports()}
        disabled={selectedReports.length === 0}
        className="mt-4 p-2 bg-red-500 text-white rounded"
      >
        Delete Selected
      </button>

      {/* JSON Editor Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Edit Form Content"
        style={customStyles}
      >
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800">Edit Medical Report Content</h2>
          
          {/* Main Title */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Main Title</label>
            <input
              type="text"
              value={formInfo?.title || ""}
              onChange={(e) => setFormInfo({...formInfo, title: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Sections */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-800">Content Sections</h3>
              <button
                onClick={addNewSection}
                className="px-3 py-1 bg-green-500 text-white rounded-md text-sm"
              >
                + Add Section
              </button>
            </div>

            {formInfo?.sections?.map((section, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <h4 className="font-medium">Section {index + 1}</h4>
                  <button
                    onClick={() => removeSection(index)}
                    className="text-red-500 text-sm"
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Content</label>
                  <textarea
                    value={section.content}
                    onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    rows="3"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Icon</label>
                  <select
                    value={section.icon}
                    onChange={(e) => handleSectionChange(index, 'icon', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="security">Security</option>
                    <option value="time">Time</option>
                    <option value="document">Document</option>
                    <option value="availability">Availability</option>
                  </select>
                </div>
              </div>
            ))}
          </div>

          {/* Help Section */}
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800">Help Section</h3>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                value={formInfo?.helpTitle || ""}
                onChange={(e) => setFormInfo({...formInfo, helpTitle: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Content</label>
              <textarea
                value={formInfo?.helpContent || ""}
                onChange={(e) => setFormInfo({...formInfo, helpContent: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Button Text</label>
              <input
                type="text"
                value={formInfo?.helpButton || ""}
                onChange={(e) => setFormInfo({...formInfo, helpButton: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateFormInfo}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default MedicalReportsManagement;